/**
 * Sponsor invite greeting personalization (mirrors Edge `parse-greeting.ts` for admin preview).
 * Greeting uses the recipient's provided first name; falls back to "Hi there,".
 */

export type GreetingPath = 'provided' | 'default';

/** First whitespace-delimited token of a (possibly full) name, capitalized; null when empty. */
export function firstNameToken(name?: string | null): string | null {
  const token = (name ?? '').trim().split(/\s+/)[0] ?? '';
  if (!token) return null;
  return token.charAt(0).toUpperCase() + token.slice(1);
}

export function buildGreeting(firstName?: string | null): { greeting: string; path: GreetingPath } {
  const first = firstNameToken(firstName);
  return first
    ? { greeting: `Hi ${first},`, path: 'provided' }
    : { greeting: 'Hi there,', path: 'default' };
}

/** Replace `{{greeting}}` in template body using the recipient's provided name (send-time / preview). */
export function personalizeSponsorInviteBody(
  body: string,
  firstName?: string | null,
): { body: string; greetingPath: GreetingPath } {
  const { greeting, path } = buildGreeting(firstName);
  return {
    body: body.replace(/\{\{greeting\}\}/g, greeting),
    greetingPath: path,
  };
}

/** Admin preview when no recipient name context: show a sample greeting (matches the send-time default). */
export const SPONSOR_INVITE_PREVIEW_GREETING = 'Hi there,';

export function applyPreviewGreeting(body: string, sampleGreeting = SPONSOR_INVITE_PREVIEW_GREETING): string {
  return body.replace(/\{\{greeting\}\}/g, sampleGreeting);
}
